import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { serviceIcon, serviceLabel, formatZAR } from "@/lib/constants";
import { Link } from "react-router-dom";
import { Navigation } from "lucide-react";

// Fix default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const redIcon = new L.DivIcon({
  html: `<div style="background:#3CFF7E;width:16px;height:16px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px #3CFF7E;"></div>`,
  className: "",
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const blueIcon = new L.DivIcon({
  html: `<div style="background:#0A0A0A;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px #0A0A0A;"></div>`,
  className: "",
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

export default function MapScreen() {
  const { user } = useAuth();
  const [coords, setCoords] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) {
      setCoords({ lat: -26.2041, lng: 28.0473 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setCoords({ lat: -26.2041, lng: 28.0473 }),
      { timeout: 7000 }
    );
  }, []);

  useEffect(() => {
    if (!coords) return;
    (async () => {
      try {
        if (user.role === "client") {
          const { data } = await api.get(`/mechanics/nearby?lat=${coords.lat}&lng=${coords.lng}`);
          setMarkers(data.filter((m) => m.location?.lat).map((m) => ({
            ...m, type: "mechanic",
          })));
        } else if (user.role === "mechanic") {
          const { data } = await api.get("/requests/nearby");
          setMarkers(data.filter((r) => r.location?.lat).map((r) => ({
            ...r, type: "request",
          })));
        }
      } finally { setLoading(false); }
    })();
  }, [coords, user.role]);

  if (!coords) {
    return (
      <div className="px-6 pt-8">
        <h1 className="font-display text-3xl font-black">Map</h1>
        <p className="text-sm text-[#9CA3AF] mt-2">Locating you...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="px-6 pt-8 pb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display mb-1">Live Map</div>
        <h1 className="font-display text-3xl font-black tracking-tight">
          {user.role === "client" ? "Mechanics nearby" : "Jobs nearby"}
        </h1>
        <p className="text-sm text-[#9CA3AF] mt-1">
          {loading ? "Loading..." : `${markers.length} results within view.`}
        </p>
      </div>

      <div className="h-[55vh] border-y border-white/10 relative">
        <MapContainer center={[coords.lat, coords.lng]} zoom={13} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          <Marker position={[coords.lat, coords.lng]} icon={redIcon}>
            <Popup>You are here</Popup>
          </Marker>
          <Circle center={[coords.lat, coords.lng]} radius={1000} pathOptions={{ color: "#3CFF7E", fillOpacity: 0.08, weight: 1 }} />
          {markers.map((m, i) => {
            const lat = m.location?.lat;
            const lng = m.location?.lng;
            if (!lat || !lng) return null;
            const key = m.id || `${m.type}-${lat}-${lng}-${i}`;
            return (
              <Marker key={key} position={[lat, lng]} icon={blueIcon}>
                <Popup>
                  {m.type === "mechanic" ? (
                    <div>
                      <div className="font-bold">{m.name}</div>
                      <div className="text-xs">{m.rating ? `${m.rating}★` : "New"} · {m.distance_km} km</div>
                    </div>
                  ) : (
                    <div>
                      <div className="font-bold">{serviceLabel(m.serviceType)} {serviceIcon(m.serviceType)}</div>
                      <div className="text-xs">{m.clientName} · {formatZAR(m.price)}</div>
                      <Link to={`/app/request/${m.id}`} className="text-[#3CFF7E] text-xs font-bold">View →</Link>
                    </div>
                  )}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      <div className="px-6 py-4 space-y-2">
        {markers.slice(0, 5).map((m, i) => (
          <div key={i} className="border border-white/10 rounded-md p-3 flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-sm">
                {m.type === "mechanic" ? m.name : serviceLabel(m.serviceType)}
              </div>
              <div className="text-xs text-[#9CA3AF]">
                {m.distance_km != null && m.distance_km < 9999 ? `${m.distance_km} km away` : ""}
                {m.type === "mechanic" && m.rating ? ` · ${m.rating}★` : ""}
                {m.type === "request" ? ` · ${formatZAR(m.price)}` : ""}
              </div>
            </div>
            {m.type === "request" && (
              <Link to={`/app/request/${m.id}`} className="text-[10px] font-display font-bold uppercase tracking-wider text-[#3CFF7E]" data-testid={`map-view-${m.id}`}>VIEW</Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
